import uuid from '@vend/vend-uuid'
import angular from 'angular'
import VendNumber, { isFinite, vn, round } from 'vend-number'
import { ApiCustomer, ApiStoreCredit } from '@vend/types'
import { PAYMENT_TYPES } from '@vend/business-components'

import { trackError } from '@vend/utilities'
import {
  recordStoreCredit,
  getCustomerStoreCredit,
} from '../components/store-credit/store-credit-resource/store-credit-resource'
import { CustomerBalances, Sale } from '../models/sale'
import {
  ReduxCurrentSale,
  selectCurrentSale,
} from '../state/slices/currentSale/currentSaleSlice'
import {
  retailerSettingsManager,
  userManager,
  webRegisterStatusManager,
} from '../ng-compat/managers'
import { store } from '../state/store'
import { client } from '../apollo'
import { getFeatureFromCache } from '../util/feature-cache'
import {
  FEATURE_CUSTOMER_BALANCE_SPLIT,
  FEATURE_ON_ACCOUNT_PAY_VIA_REGISTER,
} from '../constants'
import {
  getCustomerBalancesQuery,
  GetCustomerBalancesResult,
} from '../queries/customer-balances'
import { customerManager } from './customer-manager'

export interface StoreCreditBalance extends Partial<ApiStoreCredit> {
  balance: number
}

/**
 * Manages customer balances
 *
 * @module webregister.managers.customerBalancesManager
 */

export class CustomerBalancesManager {
  // ============================== Public API ============================== //

  /**
   * Retrieves the latest customer balances
   *
   * @todo  Ideally we should not be calling this when in offline mode, this does require us to change the display
   * of loyalty and on account totals.  Currently these display in offline mode and could potentially be out of date.
   * After we hide those in offline mode we can make this method dependant on being online
   *
   * @method getCustomerBalances
   *
   * @param  {Customer} customer
   *         The customer which is to be evaluated
   *
   * @return {Promise} A promise resolved with the given customer's balances
   */
  public async getCustomerBalances(
    customer: ApiCustomer
  ): Promise<CustomerBalances> {
    const isSplitEnabled =
      getFeatureFromCache(FEATURE_CUSTOMER_BALANCE_SPLIT) &&
      getFeatureFromCache(FEATURE_ON_ACCOUNT_PAY_VIA_REGISTER)

    const [refreshedCustomer, storeCredit, gqlBalances] = await Promise.all([
      customerManager.refreshCustomer(customer),
      this.getCustomerStoreCredit(customer) ?? { balance: 0 },
      isSplitEnabled
        ? this.getCustomerGraphqlBalances(customer.id)
        : Promise.resolve({ laybyBalance: 0, onAccountBalance: 0 }),
    ])

    const safeBalance = isFinite(refreshedCustomer.balance)
      ? vn(refreshedCustomer.balance).toNumber()
      : 0
    const accountBalance = vn(round(safeBalance.toString(), 2)).toNumber()

    const onAccountLimit = isFinite(refreshedCustomer.on_account_limit)
      ? vn(refreshedCustomer.on_account_limit as number).toNumber()
      : 0

    return {
      storeCredit: storeCredit as ApiStoreCredit,
      loyalty: refreshedCustomer.loyalty_balance ?? 0,
      // account = total owed (on-account + layby combined), always from REST balance
      account: accountBalance,
      onAccountBalance: gqlBalances?.onAccountBalance ?? 0,
      laybyBalance: gqlBalances?.laybyBalance ?? 0,
      onAccountLimit,
      // When split is on and GQL succeeded, available credit = limit + onAccountBalance (excludes layby debt).
      // When split is off, or GQL failed/offline, fall back to the legacy REST-based calculation
      // (limit + combined REST balance) — matches the behaviour of the non-split flow.
      availableOnAccountLimitBalance:
        isSplitEnabled && gqlBalances !== null
          ? VendNumber.add(onAccountLimit, gqlBalances.onAccountBalance)
          : VendNumber.add(onAccountLimit, accountBalance),
    }
  }

  /**
   * Calculate the store credit balance for a sale.
   *
   * @method calculateStoreCreditBalance
   *
   * @param {Sale} sale
   *        The sale to calculate the current store credit balance on
   *
   * @return {Number} Return a value representing the current store credit balance for the customer
   */
  public calculateStoreCreditBalance() {
    const currentSale = selectCurrentSale(store.getState())
    if (
      !currentSale.customerBalances ||
      !currentSale.customerBalances.storeCredit
    ) {
      return 0
    }

    const currentStoreCreditBalance =
      currentSale.customerBalances.storeCredit.balance
    const totalStoreCreditPayments = this.getTotalPartialPayments(
      PAYMENT_TYPES.STORE_CREDIT,
      currentSale
    )
    const adjustedStoreCreditBalance = VendNumber.subtract(
      currentStoreCreditBalance,
      totalStoreCreditPayments
    )

    return adjustedStoreCreditBalance
  }

  /**
   * Calculates the total amount of partial payments made with the specified payment type. Excludes any 'retrieved'
   * payments unless the "includeRetrievedPayments" option is passed.
   *
   * @method getTotalPartialPayments
   *
   * @param {Object} options
   *
   *        @param {Sale} sale
   *               The Sale to check conditions against
   *
   *        @param {String} paymentType
   *               The payment type to use in the lookup for partial payments
   *
   *        @param {Boolean} [includeRetrievedPayments = false]
   *               Whether to include any retrieved payments (payments part of an API hyrdated sale)
   *
   * @return {Number} The total amount of partial payments made with the specified payment type
   */
  public getTotalPartialPayments(
    paymentType: number,
    sale: Readonly<Sale> | Readonly<ReduxCurrentSale>,
    includeRetrievedPayments: boolean = false
  ) {
    if (!sale) {
      return 0
    }
    return sale.payments.reduce((total, payment) => {
      if (
        payment.paymentType.type_id === paymentType &&
        (includeRetrievedPayments || !payment.retrieved)
      ) {
        return VendNumber.add(total, payment.amount)
      } else {
        return total
      }
    }, 0)
  }

  /**
   * Issues a store credit to the provided customer
   *
   * @method issueStoreCredit
   *
   * @param  {String} customerId
   *         The customer to receive the store credit
   *
   * @param  {Object} storeCreditData
   *
   *        @param {Number} storeCreditData.amount
   *               The amount of store credit to issue
   *
   *        @param {String} storeCreditData.notes
   *               The accompanying note
   *
   * @return {Promise} Resolved once the store credit has been issued to the customer
   */
  public issueStoreCredit(
    customerId: string,
    storeCreditData: {
      amount: number
      notes: string
    }
  ) {
    const issueStoreCreditData = {
      type: 'ISSUE',
      amount: vn(round(storeCreditData.amount, 2)).toNumber(),
      user_id: userManager.getLoggedInUser().id,
      notes: storeCreditData.notes,
      client_id: uuid(),
    }

    return recordStoreCredit(customerId, issueStoreCreditData)
  }

  // ============================== Private API ============================== //

  private parseBalanceAmount(value: string | null | undefined): number {
    const parsed = Number(value)

    return Number.isFinite(parsed) ? parsed : 0
  }

  /**
   * Retrieves laybyBalance and onAccountBalance for a customer via GraphQL.
   * Only called when `on_account_layby_balance_split` and `on_account_pay_via_register` FFs are on.
   * Returns null when offline or when the request fails so the caller can fall back to the
   * legacy REST-based calculation, matching the behaviour of the non-split flow.
   *
   * TODO: If backend adds layby_balance/on_account_balance to REST /api/2.0/customers/<id>,
   * this GraphQL query can be replaced with REST fields.
   *
   * @method getCustomerGraphqlBalances
   * @param customerId - The customer ID to query
   */
  private async getCustomerGraphqlBalances(
    customerId: string
  ): Promise<{ laybyBalance: number; onAccountBalance: number } | null> {
    if (!webRegisterStatusManager.isOnline()) {
      return null
    }

    try {
      const result = await client.query<GetCustomerBalancesResult>({
        query: getCustomerBalancesQuery,
        variables: { customerId },
        fetchPolicy: 'network-only',
      })
      return {
        laybyBalance: this.parseBalanceAmount(
          result.data?.customer?.laybyBalance?.amount
        ),
        onAccountBalance: this.parseBalanceAmount(
          result.data?.customer?.onAccountBalance?.amount
        ),
      }
    } catch {
      trackError(
        '[CustomerBalancesManager] Failed to retrieve customer split balances via GraphQL',
        {
          data: { customerId },
        }
      )
      return null
    }
  }

  /**
   * Retrieves the latest customer store credit details from the api
   *
   * @method getCustomerStoreCredit
   *
   * @param  {Customer} customer
   *         The customer which is to be evaluated
   *
   * @return {Promise} A promise resolved with the given customer store credit
   */
  public getCustomerStoreCredit(
    customer: ApiCustomer
  ): Promise<StoreCreditBalance | null> {
    if (
      !webRegisterStatusManager.isOnline() ||
      !retailerSettingsManager.isStoreCreditEnabled()
    ) {
      return Promise.resolve(null)
    }

    return getCustomerStoreCredit(customer.id)
      .then(response => {
        if (response.ok) {
          return response.json()
        }

        return Promise.reject(response)
      })
      .catch(error => {
        if (error.status === 404) {
          return {
            balance: 0,
          }
        }

        trackError(
          '[CustomerBalanceManager] Failed to retrieve customer store credit data',
          {
            data: { error },
          }
        )
        return null
      })
  }
}

export const customerBalancesManager = new CustomerBalancesManager()

angular
  .module('webregister.managers')
  .factory('customerBalancesManager', () => customerBalancesManager)
